import { LoaderCircle, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { supabase } from '../../lib/supabase'

function convertirNumero(valor) {
  const numero = Number.parseFloat(valor)

  return Number.isFinite(numero) ? numero : 0
}

function ProductoModal({ producto, onClose, onGuardado }) {
  const { empresaActual } = useEmpresa()
  const esEdicion = Boolean(producto)

  const [codigo, setCodigo] = useState(producto?.codigo || '')
  const [nombre, setNombre] = useState(producto?.nombre || '')
  const [categoriaId, setCategoriaId] = useState(
    producto?.categoria_id || ''
  )
  const [unidadMedidaId, setUnidadMedidaId] = useState(
    producto?.unidad_medida_id || ''
  )
  const [descripcion, setDescripcion] = useState(
    producto?.descripcion || ''
  )
  const [stockMinimo, setStockMinimo] = useState(
    producto?.stock_minimo?.toString() || '0'
  )
  const [activo, setActivo] = useState(producto?.activo ?? true)

  const [categorias, setCategorias] = useState([])
  const [unidades, setUnidades] = useState([])
  const [cargandoOpciones, setCargandoOpciones] = useState(true)
  const [errorOpciones, setErrorOpciones] = useState('')

  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const cargarOpciones = async () => {
      if (!empresaActual?.id) return

      try {
        setCargandoOpciones(true)
        setErrorOpciones('')

        const [respuestaCategorias, respuestaUnidades] =
          await Promise.all([
            supabase
              .from('categorias_producto')
              .select('id, nombre')
              .eq('empresa_id', empresaActual.id)
              .eq('activo', true)
              .order('nombre'),

            supabase
              .from('unidades_medida')
              .select('id, codigo, nombre')
              .eq('empresa_id', empresaActual.id)
              .eq('activo', true)
              .order('codigo'),
          ])

        if (respuestaCategorias.error) {
          throw respuestaCategorias.error
        }

        if (respuestaUnidades.error) {
          throw respuestaUnidades.error
        }

        setCategorias(respuestaCategorias.data || [])
        setUnidades(respuestaUnidades.data || [])
      } catch (errorCarga) {
        console.error(
          'Error al cargar categorías y unidades:',
          errorCarga
        )

        setErrorOpciones(
          errorCarga.message ||
            'No fue posible cargar categorías y unidades.'
        )
      } finally {
        setCargandoOpciones(false)
      }
    }

    cargarOpciones()
  }, [empresaActual?.id])

  const cerrar = () => {
    if (guardando) return
    onClose()
  }

  const guardarProducto = async (event) => {
    event.preventDefault()

    if (guardando) return

    setError('')

    const codigoLimpio = codigo.trim().toUpperCase()
    const nombreLimpio = nombre.trim()
    const stockMinimoNumero = convertirNumero(stockMinimo)

    if (!codigoLimpio) {
      setError('Debes ingresar el código del producto.')
      return
    }

    if (!nombreLimpio) {
      setError('Debes ingresar el nombre del producto.')
      return
    }

    if (!unidadMedidaId) {
      setError('Debes seleccionar la unidad de medida.')
      return
    }

    if (stockMinimoNumero < 0) {
      setError('El stock mínimo no puede ser negativo.')
      return
    }

    const datos = {
      codigo: codigoLimpio,
      nombre: nombreLimpio,
      categoria_id: categoriaId || null,
      unidad_medida_id: unidadMedidaId,
      descripcion: descripcion.trim() || null,
      stock_minimo: stockMinimoNumero,
    }

    try {
      setGuardando(true)

      let productoGuardado = null

      if (esEdicion) {
        const { data, error: errorActualizar } = await supabase
          .from('productos')
          .update({ ...datos, activo })
          .eq('id', producto.id)
          .eq('empresa_id', empresaActual.id)
          .select()
          .single()

        if (errorActualizar) {
          throw errorActualizar
        }

        productoGuardado = data
      } else {
        const { data, error: errorInsertar } = await supabase
          .from('productos')
          .insert({
            empresa_id: empresaActual.id,
            ...datos,
            activo: true,
          })
          .select()
          .single()

        if (errorInsertar) {
          throw errorInsertar
        }

        productoGuardado = data
      }

      onGuardado?.(productoGuardado)
    } catch (errorGuardado) {
      console.error('Error al guardar producto:', errorGuardado)

      if (errorGuardado.code === '23505') {
        setError(
          'Ya existe un producto con ese código en esta empresa.'
        )
      } else {
        setError(
          errorGuardado.message ||
            'No fue posible guardar el producto.'
        )
      }
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          cerrar()
        }
      }}
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-start justify-between">
          <h2 className="text-xl font-bold text-slate-900">
            {esEdicion ? 'Editar producto' : 'Nuevo producto'}
          </h2>

          <button
            type="button"
            onClick={cerrar}
            disabled={guardando}
            aria-label="Cerrar"
            className="text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
          >
            <X size={22} />
          </button>
        </div>

        {cargandoOpciones ? (
          <div className="flex min-h-40 items-center justify-center">
            <LoaderCircle
              size={32}
              className="animate-spin text-emerald-600"
            />
          </div>
        ) : (
          <form
            className="space-y-5"
            onSubmit={guardarProducto}
          >
            {errorOpciones && (
              <div
                role="alert"
                className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700"
              >
                {errorOpciones}
              </div>
            )}

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="codigo"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Código
                  <span className="ml-1 text-red-500">*</span>
                </label>

                <input
                  id="codigo"
                  type="text"
                  value={codigo}
                  onChange={(event) =>
                    setCodigo(event.target.value)
                  }
                  placeholder="Ejemplo: PROD-001"
                  disabled={guardando}
                  className={estiloInput}
                />
              </div>

              <div>
                <label
                  htmlFor="nombre"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Nombre
                  <span className="ml-1 text-red-500">*</span>
                </label>

                <input
                  id="nombre"
                  type="text"
                  value={nombre}
                  onChange={(event) =>
                    setNombre(event.target.value)
                  }
                  placeholder="Ejemplo: Caja cartón 5 KG"
                  disabled={guardando}
                  className={estiloInput}
                />
              </div>

              <div>
                <label
                  htmlFor="categoria_id"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Categoría
                </label>

                <select
                  id="categoria_id"
                  value={categoriaId}
                  onChange={(event) =>
                    setCategoriaId(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                >
                  <option value="">Sin categoría</option>

                  {categorias.map((categoria) => (
                    <option
                      key={categoria.id}
                      value={categoria.id}
                    >
                      {categoria.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="unidad_medida_id"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Unidad de medida
                  <span className="ml-1 text-red-500">*</span>
                </label>

                <select
                  id="unidad_medida_id"
                  value={unidadMedidaId}
                  onChange={(event) =>
                    setUnidadMedidaId(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                >
                  <option value="">Seleccionar unidad</option>

                  {unidades.map((unidad) => (
                    <option
                      key={unidad.id}
                      value={unidad.id}
                    >
                      {unidad.codigo} — {unidad.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="stock_minimo"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Stock mínimo
                </label>

                <input
                  id="stock_minimo"
                  type="number"
                  min="0"
                  step="0.001"
                  value={stockMinimo}
                  onChange={(event) =>
                    setStockMinimo(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="descripcion"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                Descripción
              </label>

              <textarea
                id="descripcion"
                value={descripcion}
                onChange={(event) =>
                  setDescripcion(event.target.value)
                }
                rows={3}
                placeholder="Información adicional del producto"
                disabled={guardando}
                className={estiloInput}
              />
            </div>

            {esEdicion && (
              <label className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={activo}
                  onChange={(event) =>
                    setActivo(event.target.checked)
                  }
                  disabled={guardando}
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                Producto activo
              </label>
            )}

            {error && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {error}
              </div>
            )}

            <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={cerrar}
                disabled={guardando}
                className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                Cancelar
              </button>

              <button
                type="submit"
                disabled={guardando}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
              >
                {guardando && (
                  <LoaderCircle
                    size={18}
                    className="animate-spin"
                  />
                )}

                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

export default ProductoModal
